import { useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bold, Italic, Link2, Unlink } from 'lucide-react';

interface RichTextAreaProps {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export function RichTextArea({ label, value, onChange, placeholder }: RichTextAreaProps) {
  const { t } = useTranslation();
  const editorRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  // Set initial content only once
  useEffect(() => {
    if (editorRef.current && !initialized.current) {
      editorRef.current.innerHTML = value;
      initialized.current = true;
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCmd = useCallback((cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
  }, [handleInput]);

  const handleBold = () => execCmd('bold');
  const handleItalic = () => execCmd('italic');

  const handleLink = () => {
    const url = prompt(t('richText.linkPrompt', 'Enter the link URL:'));
    if (url) execCmd('createLink', url);
  };

  const handleUnlink = () => execCmd('unlink');

  return (
    <div>
      {label && <label className="mb-1.5 block text-sm font-medium text-text-primary">{label}</label>}
      <div className="rounded-lg border border-border-default focus-within:border-accent transition-colors overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 border-b border-border-default bg-bg-tertiary px-2 py-1.5">
          <button
            type="button"
            onClick={handleBold}
            className="rounded p-1.5 text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
            title={t('richText.bold', 'Bold')}
          >
            <Bold className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={handleItalic}
            className="rounded p-1.5 text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
            title={t('richText.italic', 'Italic')}
          >
            <Italic className="h-3.5 w-3.5" />
          </button>
          <div className="mx-1 h-4 w-px bg-border-default" />
          <button
            type="button"
            onClick={handleLink}
            className="rounded p-1.5 text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
            title={t('richText.link', 'Link')}
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={handleUnlink}
            className="rounded p-1.5 text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
            title={t('richText.unlink', 'Unlink')}
          >
            <Unlink className="h-3.5 w-3.5" />
          </button>
        </div>
        {/* Editable area */}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          data-placeholder={placeholder}
          className="min-h-[120px] max-h-[240px] overflow-y-auto px-3 py-2.5 text-sm text-text-primary outline-none bg-bg-field empty:before:content-[attr(data-placeholder)] empty:before:text-text-muted"
          dir="rtl"
        />
      </div>
    </div>
  );
}
